/* eslint-disable react/no-array-index-key */
import React, { useMemo } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    ColumnDef,
    flexRender,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { NSTable } from 'bricks';
import { tableValueClassName } from 'views/ProFormaTable/constants';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import { useProFormaSocketContext } from '../../../socketContext/ProFormaSocketProvider';
import './DebtServiceTable.scss';
import { IDebtService } from '../../../../../types';

export interface IDebtServiceTableProps {
    baseCashFlowColumns: ColumnDef<any>[];
}

const DebtServiceTable = ({ baseCashFlowColumns }: IDebtServiceTableProps) => {
    const {
        showCents, formatDisplayValue, operatingInfo,
        periodCadence, proForma,
    } = useProFormaSocketContext();

    const defaultDebtServiceData = {
        annualPayment: null,
        percentOfCost: null,
        percentOfStabilizedValue: null,
        totalAmount: null,
        cashFlow: [],
        yearlyCashFlow: [],
    };

    const data = useMemo(
        () => [operatingInfo?.debtService ?? defaultDebtServiceData],
        [operatingInfo?.debtService],
    );

    const cashFlowColumns: ColumnDef<any>[] = useMemo(
        () => baseCashFlowColumns.map((column, cashFlowIndex) => ({
            ...column,
            id: `debt-service-period-${column.id}`,
            accessorFn: (row: IDebtService) => {
                const series = (periodCadence === Periods.ANNUALLY
                    ? row.yearlyCashFlow
                    : row.cashFlow) as { value: number | null }[];

                return typeof row?.annualPayment === 'number'
                    ? (series?.[cashFlowIndex]?.value ?? 0)
                    : null;
            },
        })),
        [baseCashFlowColumns, periodCadence],
    );

    const columns = useMemo<ColumnDef<any>[]>(() => [
        {
            accessorKey: 'name',
            id: 'name',
            header: () => null,
            cell: () => <span className="text-dark px-1">Debt service</span>,
        },
        {
            accessorKey: 'percentOfStabilizedValue',
            id: 'percentOfStabilizedValue',
            header: () => <span>% of stab. value</span>,
            cell: ({ row, getValue }) => (
                <span className={tableValueClassName(getValue())}>
                    {getValue() === null ? '—' : formatDisplayValue(getValue(), true, 'percentage')}
                </span>
            ),
        },
        {
            accessorKey: 'percentOfCost',
            id: 'percentOfCost',
            header: () => <span>% of cost</span>,
            cell: ({ row, getValue }) => (
                <span className={tableValueClassName(getValue())}>
                    {getValue() === null ? '—' : formatDisplayValue(getValue(), true, 'percentage')}
                </span>
            ),
        },
        {
            accessorKey: 'totalAmount',
            id: 'totalAmount',
            header: () => <span>Amount</span>,
            cell: ({ row, getValue }) => (
                <span className={tableValueClassName(getValue())}>
                    {getValue() === null ? '—' : formatDisplayValue(getValue(), showCents, 'currency')}
                </span>
            ),
        },
        {
            accessorKey: 'annualPayment',
            id: 'annualPayment',
            header: () => <span className="pr-1">Annual payment</span>,
            cell: ({ row, getValue }) => (
                <span className={`${tableValueClassName(getValue())} pr-1`}>
                    {getValue() === null ? '—' : formatDisplayValue(getValue(), showCents, 'currency')}
                </span>
            ),
        },
        ...cashFlowColumns,
    ], [cashFlowColumns, showCents, operatingInfo]);

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    const rowModel = table.getRowModel();

    return (
        <NSTable className={`NSTable DebtServiceTable--${proForma.proFormaModelName?.replace(/ /g, '-')} mb-1`}>
            <thead className="NSTable__thead">
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id} className="NSTable__thead__tr">
                        {headerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__thead__tr__th p-1">
                                {header.isPlaceholder ? null : (
                                    <div>
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                        )}
                                    </div>
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody
                className={classNames('NSTable__tbody', {
                    'NSTable__tbody--pro-forma-placeholder': !rowModel.rows.length,
                })}
            >
                {rowModel.rows.map(row => (
                    <tr key={row.id} className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                        {row.getVisibleCells().map(cell => (
                            <td key={cell.id} className="NSTable__tbody__tr__td p-1">
                                {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext(),
                                )}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </NSTable>
    );
};

export default DebtServiceTable;
